import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { AchievementsSummary } from "@/components/achievements/summary";
import { requireRole } from "@/lib/session";
import { useParentContext, type ParentChild } from "@/lib/parent-context";
import {
  getParentCompanion,
  saveParentPushSubscription,
  type ParentCompanionSnapshot,
} from "@/lib/parent-companion.functions";
import {
  Sparkles, TrendingUp, AlertCircle, CalendarDays, ClipboardList,
  MessageSquare, Bell, BellOff, Printer, Clock, Flame, HeartHandshake,
  Loader2, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/parent/companion")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({
    meta: [
      { title: "Parent Companion — MAKAI" },
      { name: "description", content: "A warm weekly companion for parents — how your child is learning, what to celebrate, and how to help this week." },
      { property: "og:title", content: "Parent Companion — MAKAI" },
      { property: "og:description", content: "How your child is learning this week — in one calm, honest place." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ParentCompanion,
});

function fmtWhen(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays > 1 && diffDays < 7) return `In ${diffDays} days`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ParentCompanion() {
  const { ctx, loading } = useParentContext();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [snap, setSnap] = useState<ParentCompanionSnapshot | null>(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const fetchCompanion = useServerFn(getParentCompanion);
  const persistPush = useServerFn(saveParentPushSubscription);
  const notifiedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!ctx || ctx.children.length === 0) return;
    if (!activeId) setActiveId(ctx.children[0].learnerId);
  }, [ctx, activeId]);

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setSnapLoading(true);
    fetchCompanion({ data: { learnerId: activeId } })
      .then((s) => { if (!cancelled) setSnap(s); })
      .catch(() => { if (!cancelled) setSnap(null); })
      .finally(() => { if (!cancelled) setSnapLoading(false); });
    return () => { cancelled = true; };
  }, [activeId, fetchCompanion]);

  // Fire local notifications for new high-priority alerts once per session.
  useEffect(() => {
    if (!snap || typeof window === "undefined") return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    for (const a of snap.alerts) {
      if (a.priority < 60) continue;
      const key = `${snap.learner.id}:${a.id}`;
      if (notifiedRef.current.has(key)) continue;
      notifiedRef.current.add(key);
      try {
        new Notification(a.title, { body: a.body, tag: key, icon: "/icons/icon-192.png" });
      } catch { /* noop */ }
    }
  }, [snap]);

  const activeChild: ParentChild | null = useMemo(
    () => ctx?.children.find((c) => c.learnerId === activeId) ?? null,
    [ctx, activeId],
  );

  if (loading) {
    return (
      <AppShell role="parent" title="Companion">
        <div className="grid place-items-center p-10 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AppShell>
    );
  }
  if (!ctx || ctx.children.length === 0) {
    return (
      <AppShell role="parent" title="Companion">
        <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <HeartHandshake className="mx-auto h-8 w-8 text-primary" />
          <h1 className="mt-3 font-display text-xl font-bold">Link a child to begin</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The Parent Companion becomes available as soon as a child is linked to your account.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      role="parent"
      title="Parent Companion"
      subtitle={activeChild ? `A calm weekly view of ${activeChild.firstName}'s learning.` : undefined}
    >
      <div className="mx-auto max-w-4xl space-y-6 print:max-w-none">
        {/* Child switcher + controls */}
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          {ctx.children.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {ctx.children.map((c) => (
                <button
                  key={c.learnerId}
                  onClick={() => setActiveId(c.learnerId)}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition",
                    c.learnerId === activeId
                      ? "border-primary bg-primary-soft text-primary font-semibold"
                      : "border-border bg-card text-muted-foreground hover:text-foreground",
                  )}
                >
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                    {c.initials}
                  </span>
                  {c.firstName}
                </button>
              ))}
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <NotifyToggle onSubscribed={(sub) => {
              persistPush({
                data: {
                  endpoint: sub.endpoint,
                  p256dh: sub.p256dh,
                  auth: sub.auth,
                  userAgent: navigator.userAgent,
                },
              }).catch(() => { /* non-fatal */ });
            }} />
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted"
            >
              <Printer className="h-3.5 w-3.5" /> Share as PDF
            </button>
          </div>
        </div>

        {snapLoading && !snap && (
          <div className="grid place-items-center rounded-2xl border border-border bg-card p-10 shadow-soft">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}

        {snap && (
          <>
            {/* Hero */}
            <section className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary-soft/60 to-background p-6 shadow-soft">
              <div className="flex items-start gap-4">
                {snap.learner.photoUrl ? (
                  <img loading="lazy" decoding="async"
                    src={snap.learner.photoUrl}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-primary/30"
                  />
                ) : (
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-lg font-bold text-primary">
                    {snap.learner.firstName.slice(0, 1)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                    Your weekly companion
                  </div>
                  <h1 className="mt-1 font-display text-2xl font-bold leading-tight">
                    How {snap.learner.firstName} is learning this week
                  </h1>
                  {snap.learner.gradeLabel && (
                    <div className="mt-1 text-sm text-muted-foreground">{snap.learner.gradeLabel}</div>
                  )}
                </div>
              </div>

              {snap.week.improvementHeadline && (
                <div className="mt-5 flex items-start gap-3 rounded-xl bg-card p-4 shadow-soft">
                  <TrendingUp className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                      A win to celebrate
                    </div>
                    <p className="mt-1 text-sm font-semibold">
                      {snap.learner.firstName} {snap.week.improvementHeadline.toLowerCase()} this week.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Learning activities" value={String(snap.week.activities)} icon={Sparkles} />
                <Stat
                  label="Minutes learning"
                  value={String(snap.week.minutesLearning)}
                  icon={Clock}
                />
                <Stat
                  label="Streak"
                  value={`${snap.streak.currentDays}d`}
                  icon={Flame}
                  tone={snap.streak.currentDays >= 3 ? "gold" : "muted"}
                />
                <Stat
                  label="Skills improved"
                  value={String(snap.week.skillsImproved)}
                  icon={TrendingUp}
                />
              </div>
            </section>

            {/* AI Summary */}
            {snap.ai.parentSummary && (
              <SectionCard title="Makai's note to you" icon={Sparkles} accent>
                <p className="text-sm leading-relaxed">{snap.ai.parentSummary}</p>
              </SectionCard>
            )}

            {/* Strengths + Attention */}
            <div className="grid gap-5 md:grid-cols-2">
              <SectionCard title="What's going well" icon={TrendingUp}>
                {snap.strengths.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Still building evidence. Come back after a few more learning sessions.
                  </p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {snap.strengths.map((s) => (
                      <li key={s.label} className="flex items-center gap-2">
                        <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-primary">
                          <TrendingUp className="h-3.5 w-3.5" />
                        </span>
                        <span className="font-medium">{s.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
              <SectionCard title="A little help would go a long way" icon={AlertCircle}>
                {snap.attention.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing urgent right now. Keep going.</p>
                ) : (
                  <ul className="space-y-3 text-sm">
                    {snap.attention.map((a) => (
                      <li key={a.label}>
                        <div className="font-semibold">{a.label}</div>
                        <div className="text-xs text-muted-foreground">{a.why}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            {/* Recommendations + Questions */}
            <div className="grid gap-5 md:grid-cols-2">
              <SectionCard title="Gentle recommendations" icon={HeartHandshake}>
                {snap.ai.recommendations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    A quiet week is a good week. Keep encouraging steady practice.
                  </p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {snap.ai.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
              <SectionCard title="Questions you can ask at home" icon={MessageSquare}>
                {snap.ai.questionsToAsk.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Try: "What was the best moment of your school day?"
                  </p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {snap.ai.questionsToAsk.map((q, i) => (
                      <li key={i} className="rounded-lg bg-muted/50 px-3 py-2">"{q}"</li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            {/* Homework */}
            <SectionCard title="Homework this week" icon={ClipboardList}>
              {snap.homework.length === 0 ? (
                <p className="text-sm text-muted-foreground">No homework due in the next two weeks.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {snap.homework.map((h) => (
                    <li key={h.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                      <div>
                        <div className="font-semibold">{h.title}</div>
                        {h.subject && <div className="text-xs text-muted-foreground">{h.subject}</div>}
                      </div>
                      {h.due && (
                        <span className="shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-semibold text-primary">
                          {fmtWhen(h.due)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {snap.homeworkSuggestions.length > 0 && (
                <div className="mt-3 rounded-xl bg-primary-soft/40 p-3 text-xs text-primary">
                  <strong className="font-semibold">Try this:</strong>{" "}
                  {snap.homeworkSuggestions[0]}
                </div>
              )}
            </SectionCard>

            {/* Upcoming assessments */}
            <SectionCard title="Upcoming assessments" icon={CalendarDays}>
              {snap.upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing scheduled in the next 30 days.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {snap.upcoming.map((u) => (
                    <li key={u.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                      <div>
                        <div className="font-semibold">{u.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {u.subject ?? u.kind}
                        </div>
                      </div>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold">
                        {fmtWhen(u.when)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            {/* Teacher messages */}
            <SectionCard title="Notes from teachers" icon={MessageSquare}>
              {snap.teacherMessages.length === 0 ? (
                <p className="text-sm text-muted-foreground">No new notes from teachers.</p>
              ) : (
                <ul className="space-y-3 text-sm">
                  {snap.teacherMessages.map((m) => (
                    <li key={m.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold">{m.subject ?? "Note"}</div>
                        <span className="text-[11px] text-muted-foreground">{fmtWhen(m.createdAt)}</span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{m.body}</p>
                      <div className="mt-1 text-[11px] text-muted-foreground">— {m.sender}</div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            {activeId && <AchievementsSummary learnerId={activeId} title={`${activeChild?.firstName ?? "Child"}'s medals`} />}


            <p className="pt-4 text-center text-[11px] text-muted-foreground print:mt-6">
              Generated {new Date(snap.generatedAt).toLocaleString()} · MAKAI Parent Companion
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Stat({
  label, value, icon: Icon, tone,
}: {
  label: string;
  value: string;
  icon: typeof Sparkles;
  tone?: "gold" | "muted";
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="flex items-center gap-2">
        <Icon className={cn("h-4 w-4", tone === "gold" ? "text-gold-foreground" : "text-primary")} />
        <span className="font-display text-2xl font-bold">{value}</span>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function SectionCard({
  title, icon: Icon, accent, children,
}: {
  title: string;
  icon: typeof Sparkles;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-2xl border p-5 shadow-soft",
        accent ? "border-primary/20 bg-primary-soft/30" : "border-border bg-card",
      )}
    >
      <div className={cn(
        "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]",
        accent ? "text-primary" : "text-muted-foreground",
      )}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function NotifyToggle({
  onSubscribed,
}: {
  onSubscribed: (sub: { endpoint: string; p256dh: string | null; auth: string | null }) => void;
}) {
  const [state, setState] = useState<"unsupported" | "denied" | "off" | "on">("off");

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "granted") setState("on");
    else if (Notification.permission === "denied") setState("denied");
    else setState("off");
  }, []);

  const enable = async () => {
    if (!("Notification" in window)) return;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { setState(perm === "denied" ? "denied" : "off"); return; }
    setState("on");
    // Try Web Push subscription if a service worker + VAPID key are available.
    try {
      const vapid = (import.meta as any).env?.VITE_VAPID_PUBLIC_KEY as string | undefined;
      if (!vapid || !("serviceWorker" in navigator)) return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapid,
      });
      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      if (json.endpoint) {
        onSubscribed({
          endpoint: json.endpoint,
          p256dh: json.keys?.p256dh ?? null,
          auth: json.keys?.auth ?? null,
        });
      }
    } catch { /* non-fatal */ }
  };

  if (state === "unsupported") return null;
  if (state === "on") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-primary-soft px-3 py-1.5 text-sm text-primary">
        <Bell className="h-3.5 w-3.5" /> Notifications on
      </span>
    );
  }
  if (state === "denied") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1.5 text-sm text-muted-foreground">
        <BellOff className="h-3.5 w-3.5" /> Blocked in browser
      </span>
    );
  }
  return (
    <button
      onClick={enable}
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted"
    >
      <Bell className="h-3.5 w-3.5" /> Turn on notifications
    </button>
  );
}
